export type HostedAuthMode = "legacy_api_key" | "embed_session";

export interface LegacyHostedAuth {
  "X-API-KEY": string;
  applicant_id?: string;
  product_selection?: string[];
}

export interface EmbedSessionHostedAuth {
  auth_mode: "embed_session";
  client_id: string;
  embed_key: string;
  applicant_id?: string;
  product_selection?: string[];
}

export interface NormalizedHostedAuth {
  mode: HostedAuthMode;
  legacyApiKey?: string;
  clientId?: string;
  embedKey?: string;
  applicantId?: string;
  productSelection?: string[];
}

const getStringValue = (value: unknown): string | undefined =>
  typeof value === "string" && value.trim() ? value : undefined;

const getProductSelection = (value: unknown): string[] | undefined =>
  Array.isArray(value) ? value.filter((item) => typeof item === "string") : undefined;

export function normalizeHostedAuth(rawAuth: any): NormalizedHostedAuth {
  if (!rawAuth || typeof rawAuth !== "object") {
    throw new Error("Hosted auth config is missing.");
  }

  const explicitMode = rawAuth.auth_mode ?? rawAuth.authMode;
  const legacyApiKey = getStringValue(rawAuth["X-API-KEY"]);
  const clientId = getStringValue(rawAuth.client_id ?? rawAuth.clientId);
  const embedKey = getStringValue(rawAuth.embed_key ?? rawAuth.embedKey);
  const hasEmbedSessionFields =
    explicitMode === "embed_session" || Boolean(clientId) || Boolean(embedKey);

  if (explicitMode === "embed_session") {
    if (!clientId) {
      throw new Error("Hosted embed session config is missing client_id.");
    }
    if (!embedKey) {
      throw new Error("Hosted embed session config is missing embed_key.");
    }
    return {
      mode: "embed_session",
      clientId,
      embedKey,
      applicantId: getStringValue(rawAuth.applicant_id ?? rawAuth.applicantId),
      productSelection: getProductSelection(
        rawAuth.product_selection ?? rawAuth.productSelection
      ),
    };
  }

  if (legacyApiKey && !hasEmbedSessionFields) {
    return {
      mode: "legacy_api_key",
      legacyApiKey,
      applicantId: getStringValue(rawAuth.applicant_id ?? rawAuth.applicantId),
      productSelection: getProductSelection(
        rawAuth.product_selection ?? rawAuth.productSelection
      ),
    };
  }

  if (legacyApiKey && hasEmbedSessionFields) {
    throw new Error("Hosted auth config has ambiguous auth modes.");
  }

  throw new Error("Hosted auth config is invalid.");
}

export function getUrlOrigin(url?: string): string | null {
  if (!url) {
    return null;
  }
  try {
    return new URL(url).origin;
  } catch {
    return null;
  }
}

export function getSafeTargetOrigin(
  clientUrl?: string,
  verifiedParentOrigin?: string,
  mode?: HostedAuthMode | null
): string | null {
  if (
    verifiedParentOrigin &&
    verifiedParentOrigin !== "*" &&
    verifiedParentOrigin !== "null"
  ) {
    return verifiedParentOrigin;
  }

  if (mode === "embed_session") {
    return null;
  }

  const parsedOrigin = getUrlOrigin(clientUrl);
  if (parsedOrigin && parsedOrigin !== "*") {
    return parsedOrigin;
  }

  return null;
}

export function getHostedInsuranceAuthHeaders(state: any): Record<string, string> {
  const clientState = state?.client;
  if (clientState?.hostedAuthMode === "embed_session") {
    return clientState.embedAccessToken
      ? { Authorization: `Bearer ${clientState.embedAccessToken}` }
      : {};
  }

  if (clientState?.hostedAuthMode === "legacy_api_key" || clientState?.clientAPIKey) {
    return clientState.clientAPIKey ? { "X-API-KEY": clientState.clientAPIKey } : {};
  }

  return {};
}

export function isEmbedSessionTokenExpiring(
  expiresAt?: number,
  skewMs = 60 * 1000
): boolean {
  return Boolean(expiresAt && expiresAt <= Date.now() + skewMs);
}

export function getEmbedAuthErrorCode(error: any): string | undefined {
  return (
    error?.response?.data?.code ??
    error?.response?.data?.error_code ??
    error?.response?.data?.error?.code ??
    error?.response?.data?.detail?.code
  );
}

export function getEmbedFailureReason(errorOrCode: any): string {
  const code =
    typeof errorOrCode === "string"
      ? errorOrCode
      : getEmbedAuthErrorCode(errorOrCode);

  switch (code) {
    case "EMBED_KEY_EXPIRED":
      return "embed_key_expired";
    case "EMBED_KEY_INVALID":
      return "embed_key_invalid";
    case "EMBED_KEY_REVOKED":
      return "embed_key_revoked";
    case "EMBED_ORIGIN_NOT_ALLOWED":
      return "embed_origin_not_allowed";
    case "EMBED_PRODUCT_NOT_ALLOWED":
      return "embed_product_not_allowed";
    case "EMBED_TOKEN_EXPIRED":
      return "embed_token_expired";
    case "EMBED_SESSION_EXPIRED":
      return "embed_session_expired";
    default:
      return "secure_session_failed";
  }
}
